import { Component, OnInit, OnDestroy, ChangeDetectorRef, inject } from '@angular/core';
import { RouterOutlet } from '@angular/router';
import { ErrorToastComponent } from './shared/components/error-toast/error-toast.component';
import { RateLimitAlertComponent } from './shared/components/rate-limit-alert/rate-limit-alert.component';
import { SessionService } from './auth/session.service';
import { SessionMonitorService } from './shared/services/session-monitor.service';
import { TokenRefreshService } from './services/token-refresh.service';
import { AuthService } from './auth/auth.service';
import { OrgContextNavigationService } from './shared/services/org-context-navigation.service';
import { SessionReplayService } from './shared/session-replay/session-replay.service';
import { Router, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs/operators';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [RouterOutlet, ErrorToastComponent, RateLimitAlertComponent],
  templateUrl: './app.component.html'
})
export class AppComponent implements OnInit, OnDestroy {
  title = 'coursebuilder';
  private routerSubscription?: Subscription;

  // Initializes org-context navigation guard (self-initializes via constructor subscription)
  private orgContextNav = inject(OrgContextNavigationService);
  private sessionReplay = inject(SessionReplayService);

  constructor(
    private sessionService: SessionService,
    private sessionMonitorService: SessionMonitorService,
    private tokenRefreshService: TokenRefreshService,
    private authService: AuthService,
    private router: Router,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    // Record every browsing session for QA model training (docs/session-replay.md)
    void this.sessionReplay.start();

    // Prevent scroll/trackpad from changing number input values (KAN-100)
    document.addEventListener('wheel', (e) => {
      if (e.target instanceof HTMLInputElement && e.target.type === 'number') {
        e.target.blur();
      }
    }, { passive: true });

    // Start session monitoring services if user is authenticated
    if (this.authService.isAuthenticated()) {
      this.sessionService.startSessionMonitoring();
      this.sessionMonitorService.startMonitoring();
      this.tokenRefreshService.startTokenRefreshMonitoring();
    }

    // Monitor route changes to start/stop session monitoring
    this.routerSubscription = this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe({
        next: () => {
          if (this.authService.isAuthenticated()) {
            this.sessionService.startSessionMonitoring();
            this.sessionMonitorService.startMonitoring();
            this.tokenRefreshService.startTokenRefreshMonitoring();
          } else {
            this.sessionService.stopSessionMonitoring();
            this.sessionMonitorService.stopMonitoring();
            this.tokenRefreshService.stopTokenRefreshMonitoring();
          }
        },
        error: (err) => {
          // Surface (but don't toast) — the root component can't recover from
          // a broken router stream, but we must not silently swallow auth/router
          // failures. The error toast lives downstream of NavigationEnd events
          // anyway, so logging is the right floor.
          console.error('[AppComponent] router.events stream errored:', err);
        }
      });
  }

  ngOnDestroy(): void {
    this.sessionReplay.stop();
    this.sessionService.stopSessionMonitoring();
    this.sessionMonitorService.stopMonitoring();
    this.tokenRefreshService.stopTokenRefreshMonitoring();
    if (this.routerSubscription) {
      this.routerSubscription.unsubscribe();
    }
  }
}
